import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Plus, 
  Activity, 
 
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Target,
  BarChart3,
  Eye,
  Settings,
  Play,
  Pause,
  Square
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function DemoLiveTrackingPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')

  // Mock实盘追踪数据
  const mockLiveTracking = [
    {
      id: 1,
      strategy_name: '趋势跟踪策略A',
      platform: 'Binance',
      status: 'active',
      start_date: '2024-01-15',
      current_balance: 12500,
      total_pnl: 2500,
      daily_pnl: 125,
      win_rate: 68.5,
      total_trades: 45,
      active_trades: 2,
      risk_level: 'medium'
    },
    {
      id: 2,
      strategy_name: '网格交易策略B',
      platform: 'OKX',
      status: 'active',
      start_date: '2024-02-01',
      current_balance: 8900,
      total_pnl: 900,
      daily_pnl: -50,
      win_rate: 72.1,
      total_trades: 89,
      active_trades: 5,
      risk_level: 'low'
    },
    {
      id: 3,
      strategy_name: '均值回归策略C',
      platform: 'Bybit',
      status: 'paused',
      start_date: '2024-03-10',
      current_balance: 7600,
      total_pnl: -400,
      daily_pnl: -25,
      win_rate: 55.2,
      total_trades: 67,
      active_trades: 0,
      risk_level: 'high'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'paused':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      case 'stopped':
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return '运行中'
      case 'paused':
        return '已暂停'
      case 'stopped':
        return '已停止'
      default:
        return '未知'
    }
  }

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      case 'high':
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getRiskLevelLabel = (level: string) => {
    switch (level) {
      case 'low':
        return '低风险'
      case 'medium':
        return '中风险'
      case 'high':
        return '高风险'
      default:
        return '未知'
    }
  }

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
              <Activity className="w-6 h-6 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">运行中策略</p>
              <p className="text-2xl font-bold text-gradient">
                {mockLiveTracking.filter(t => t.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">总盈亏</p>
              <p className="text-2xl font-bold text-gradient">
                ${mockLiveTracking.reduce((sum, t) => sum + t.total_pnl, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/30">
              <Target className="w-6 h-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">活跃交易</p>
              <p className="text-2xl font-bold text-gradient">
                {mockLiveTracking.reduce((sum, t) => sum + t.active_trades, 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
              <BarChart3 className="w-6 h-6 text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">平均胜率</p>
              <p className="text-2xl font-bold text-gradient">
                {(mockLiveTracking.reduce((sum, t) => sum + t.win_rate, 0) / mockLiveTracking.length).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 实盘追踪列表 */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-ocean-primary">实盘追踪</h3>
          <div className="flex space-x-3">
            <button className="btn-secondary flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              设置
            </button>
            <button className="btn-primary flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              添加追踪
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-ocean-secondary font-medium">策略名称</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">交易平台</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">当前余额</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">总盈亏</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">今日盈亏</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">活跃交易</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">风险等级</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">状态</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {mockLiveTracking.map((tracking) => (
                <tr key={tracking.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">{tracking.strategy_name}</div>
                    <div className="text-ocean-secondary text-sm">开始于 {tracking.start_date}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">{tracking.platform}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">${tracking.current_balance.toLocaleString()}</div>
                  </td>
                  <td className={`p-4 font-semibold ${
                    tracking.total_pnl > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {tracking.total_pnl > 0 ? '+' : ''}${tracking.total_pnl.toLocaleString()}
                  </td>
                  <td className={`p-4 font-semibold ${
                    tracking.daily_pnl > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {tracking.daily_pnl > 0 ? '+' : ''}${tracking.daily_pnl.toLocaleString()}
                  </td>
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">{tracking.active_trades}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(tracking.risk_level)}`}>
                      {getRiskLevelLabel(tracking.risk_level)}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tracking.status)}`}>
                      {getStatusLabel(tracking.status)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <button className="text-violet-300 hover:text-violet-400 p-1">
                        <Eye className="w-4 h-4" />
                      </button>
                      {tracking.status === 'active' ? (
                        <button className="text-yellow-300 hover:text-yellow-400 p-1">
                          <Pause className="w-4 h-4" />
                        </button>
                      ) : (
                        <button className="text-green-300 hover:text-green-400 p-1">
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      <button className="text-red-300 hover:text-red-400 p-1">
                        <Square className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">实时盈亏趋势</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[
              { time: '09:00', pnl: 1200 },
              { time: '10:00', pnl: 1350 },
              { time: '11:00', pnl: 1100 },
              { time: '12:00', pnl: 1400 },
              { time: '13:00', pnl: 1600 },
              { time: '14:00', pnl: 1800 },
              { time: '15:00', pnl: 2000 },
              { time: '16:00', pnl: 2500 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Line type="monotone" dataKey="pnl" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">策略表现对比</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { strategy: '策略A', return: 25.6, trades: 45 },
              { strategy: '策略B', return: 18.3, trades: 89 },
              { strategy: '策略C', return: 12.7, trades: 67 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="strategy" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Legend />
              <Bar dataKey="return" fill="#3b82f6" name="收益率 (%)" />
              <Bar dataKey="trades" fill="#8b5cf6" name="交易次数" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">风险监控</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-xl border border-green-500/20">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                <span className="text-ocean-primary">系统运行正常</span>
              </div>
              <span className="text-green-400 text-sm">正常</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mr-3" />
                <span className="text-ocean-primary">策略C回撤较大</span>
              </div>
              <span className="text-yellow-400 text-sm">警告</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-blue-400 mr-3" />
                <span className="text-ocean-primary">策略B暂停中</span>
              </div>
              <span className="text-blue-400 text-sm">暂停</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">平台分布</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { platform: 'Binance', balance: 12500, strategies: 1 },
              { platform: 'OKX', balance: 8900, strategies: 1 },
              { platform: 'Bybit', balance: 7600, strategies: 1 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="platform" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Legend />
              <Bar dataKey="balance" fill="#f59e0b" name="余额 ($)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab()
      case 'analytics':
        return renderAnalyticsTab()
      default:
        return renderOverviewTab()
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/demo')}
              className="btn-secondary flex items-center justify-center"
              title="返回主页"
            >
              <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">返回主页</span>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gradient">实盘追踪</h1>
              <p className="text-ocean-secondary">实时监控和管理您的实盘交易策略</p>
            </div>
          </div>
        </div>

        {/* 标签页导航 */}
        <div className="card mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              概览
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'analytics'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              数据分析
            </button>
          </div>
        </div>

        {/* 标签页内容 */}
        {renderTabContent()}
      </main>
    </div>
  )
} 